import React from "react"
import {FormProvider, useForm} from "react-hook-form"
import {Trans, useTranslation} from "react-i18next"
import {useNavigate} from "react-router"
import {zodResolver} from "@hookform/resolvers/zod"
import {useQueryClient} from "@tanstack/react-query"
import {isAxiosError} from "axios"
import {z} from "zod"

import Button, {ButtonForm} from "../../../components/Button.tsx"
import {HeroLayout} from "../../../components/HeroLayout.tsx"
import {LayoutCard} from "../../../components/LayoutCard.tsx"
import {Link} from "../../../components/Link.tsx"
import {useVerifySaaSCompanyMutation} from "../../../queries/saasCompany.ts"
import {queryKey} from "../../../services"
import {useParam} from "../../../utils/hooks.tsx"
import {setFormErrorsFromAxios} from "../../../utils/validation.ts"
import {DetailsPage} from "./subpages/DetailsPage.tsx"
import {PasswordPage} from "./subpages/PasswordPage.tsx"
import {EPage, mergedValidationSchema, steps} from "./AccountCreatePage.utils.ts"

export const AccountCreatePage: React.FC = () => {
  const {t} = useTranslation()

  const [page, setPage] = React.useState<EPage>(0)

  const methods = useForm<z.infer<typeof mergedValidationSchema>>({
    mode: "onTouched",
    resolver: zodResolver(mergedValidationSchema),
  })

  const token = useParam("invitation_token", true)

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const verifySaaSCompanyMutation = useVerifySaaSCompanyMutation()

  const handleSubmit = React.useCallback(async () => {
    const values = methods.getValues()

    if (!token || !values.privacyPolicyAgreed) {
      return
    }

    try {
      await verifySaaSCompanyMutation.mutateAsync({
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password,
        invitation_token: token,
      })

      await queryClient.refetchQueries({queryKey: queryKey.userSettings})
      navigate("/")
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 401) {
        methods.setError("root", {message: t("Signup_SaaSCompany_AccountCreatePage_NoToken_BadToken")})
        return
      }

      setFormErrorsFromAxios(e, methods.setError)
    }
  }, [methods, navigate, queryClient, t, token, verifySaaSCompanyMutation])

  const isFirstPage = Number(page) === 0
  const isLastPage = Number(page) === Object.values(steps).length - 1

  const valuesWatch = methods.watch()
  const isStepValid = React.useMemo(() => {
    const step = steps[page]

    return step.validationSchema.safeParse(valuesWatch).success
  }, [valuesWatch, page])

  const goToPreviousPage = React.useCallback(() => {
    setPage(p => p - 1)
  }, [])

  const goToNextPage = React.useCallback(() => {
    const values = methods.getValues()
    const step = steps[page]
    const fields = Object.keys(step.validationSchema.shape) as Array<keyof typeof step.validationSchema.shape>

    for (const field of fields) {
      methods.setValue(field, values[field], {shouldTouch: true, shouldDirty: true, shouldValidate: true})
    }

    if (isStepValid) {
      setPage(p => p + 1)
    }
  }, [isStepValid, methods, page])

  if (!token) {
    return (
      <HeroLayout>
        <LayoutCard title={t("Signup_SaaSCompany_AccountCreatePage_NoToken_Title")}>
          {t("Signup_SaaSCompany_AccountCreatePage_NoToken_Text")}
        </LayoutCard>
      </HeroLayout>
    )
  }

  return (
    <HeroLayout
      leftAction={
        !isFirstPage && (
          <Link onClick={goToPreviousPage} flipUnderline variant={"neutral"}>
            {t("Signup_SaaSCompany_AccountCreatePage_PreviousPage")}
          </Link>
        )
      }
    >
      <LayoutCard
        step={page === EPage.DETAILS ? 1 : 2}
        total={2}
        title={t("Signup_SaaSCompany_AccountCreatePage_Title")}
      >
        <span>{t("Signup_SaaSCompany_AccountCreatePage_Text")}</span>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)} className={"flex flex-col gap-2"}>
            {page === EPage.DETAILS && <DetailsPage />}
            {page === EPage.PASSWORD && <PasswordPage />}

            <div className={"flex flex-col gap-4"}>
              {isLastPage ? (
                <ButtonForm fullWidth disabled={!methods.formState.isValid}>
                  {t("Signup_SaaSCompany_AccountCreatePage_SubmitButton")}
                </ButtonForm>
              ) : (
                <Button fullWidth onClick={goToNextPage} disabled={!isStepValid}>
                  {t("Signup_SaaSCompany_AccountCreatePage_NextButton")}
                </Button>
              )}
            </div>
          </form>
        </FormProvider>
        <div className={"text-center text-sm"}>
          <Trans i18nKey={"Signup_Login"} components={{loginLink: <Link to={"/log-in"} flipUnderline />}} />
        </div>
      </LayoutCard>
    </HeroLayout>
  )
}
